/**
 * Platform event definitions — catalog v1 (commerce-platform Phase 0).
 *
 * Registered here: `email:sent`, `email:failed` (both auditable) and
 * `audit:recorded` (auditable:false — recording audit rows for the audit
 * event itself would recurse forever; it is observability-only and is also
 * excluded from realtime fan-out via `FANOUT_EXCLUDED_EVENT_TYPES`).
 */
import {
  defineEvent,
  asEventRecord,
  optionalNumber,
  optionalString,
  requireString,
  requireStringArray,
  type EventDefinition,
  type EventSchema,
} from './registry';

export interface EmailSentPayload {
  messageId: string;
  to: string[];
  subject: string;
  sentAt: string;
  providerMessageId?: string;
  orgId?: string;
}

export interface EmailFailedPayload {
  messageId: string;
  to: string[];
  error: string;
  failedAt: string;
  subject?: string;
  attempts?: number;
  orgId?: string;
}

export interface AuditRecordedPayload {
  recordId: string;
  orgId: string;
  action: string;
  occurredAt: string;
  actorType?: string;
  eventId?: string;
}

const emailSentSchema: EventSchema<EmailSentPayload> = {
  parse(data: unknown): EmailSentPayload {
    const record = asEventRecord(data, 'email:sent');
    return {
      messageId: requireString(record.messageId, 'messageId', 'email:sent'),
      to: requireStringArray(record.to, 'to', 'email:sent'),
      subject: requireString(record.subject, 'subject', 'email:sent'),
      sentAt: requireString(record.sentAt, 'sentAt', 'email:sent'),
      providerMessageId: optionalString(record.providerMessageId, 'providerMessageId', 'email:sent'),
      orgId: optionalString(record.orgId, 'orgId', 'email:sent'),
    };
  },
};

const emailFailedSchema: EventSchema<EmailFailedPayload> = {
  parse(data: unknown): EmailFailedPayload {
    const record = asEventRecord(data, 'email:failed');
    return {
      messageId: requireString(record.messageId, 'messageId', 'email:failed'),
      to: requireStringArray(record.to, 'to', 'email:failed'),
      error: requireString(record.error, 'error', 'email:failed'),
      failedAt: requireString(record.failedAt, 'failedAt', 'email:failed'),
      subject: optionalString(record.subject, 'subject', 'email:failed'),
      attempts: optionalNumber(record.attempts, 'attempts', 'email:failed'),
      orgId: optionalString(record.orgId, 'orgId', 'email:failed'),
    };
  },
};

const auditRecordedSchema: EventSchema<AuditRecordedPayload> = {
  parse(data: unknown): AuditRecordedPayload {
    const record = asEventRecord(data, 'audit:recorded');
    return {
      recordId: requireString(record.recordId, 'recordId', 'audit:recorded'),
      orgId: requireString(record.orgId, 'orgId', 'audit:recorded'),
      action: requireString(record.action, 'action', 'audit:recorded'),
      occurredAt: requireString(record.occurredAt, 'occurredAt', 'audit:recorded'),
      actorType: optionalString(record.actorType, 'actorType', 'audit:recorded'),
      eventId: optionalString(record.eventId, 'eventId', 'audit:recorded'),
    };
  },
};

export const EMAIL_SENT_EVENT: EventDefinition<EmailSentPayload> = defineEvent<EmailSentPayload>({
  type: 'email:sent',
  version: 1,
  schema: emailSentSchema,
  auditable: true,
  description: 'An outbound email message was accepted by the SMTP transport (email service send pipeline completed).',
  since: '1.0.0',
});

export const EMAIL_FAILED_EVENT: EventDefinition<EmailFailedPayload> = defineEvent<EmailFailedPayload>({
  type: 'email:failed',
  version: 1,
  schema: emailFailedSchema,
  auditable: true,
  description: 'An outbound email message permanently failed after all transport retries.',
  since: '1.0.0',
});

export const AUDIT_RECORDED_EVENT: EventDefinition<AuditRecordedPayload> = defineEvent<AuditRecordedPayload>({
  type: 'audit:recorded',
  version: 1,
  schema: auditRecordedSchema,
  auditable: false,
  description:
    'An audit record was persisted by the audit service. Observability-only: never audited itself (recursion) and never fanned out to realtime rooms.',
  since: '1.0.0',
});

export const PLATFORM_EVENTS: Readonly<Record<string, EventDefinition>> = {
  [EMAIL_SENT_EVENT.type]: EMAIL_SENT_EVENT,
  [EMAIL_FAILED_EVENT.type]: EMAIL_FAILED_EVENT,
  [AUDIT_RECORDED_EVENT.type]: AUDIT_RECORDED_EVENT,
};
